import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Download,
  ChevronDown,
  ChevronUp,
  Filter,
  Plus,
  X,
  Users,
  Building2,
  Save,
  MoreVertical,
  Play,
  Edit,
  Copy,
  Eye,
  Trash2,
  Loader2,
  Check,
} from "lucide-react";
import {
  apiService,
  Contact,
  CustomField,
  CustomFieldOption,
  CompanyCustomField,
  CompanyCustomFieldOption,
} from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/authStore";
import SaveConfigModal from "@/components/SaveConfigModal";

interface ExportableContact extends Contact {
  company_name?: string;
  company_website?: string;
  company_postal_code?: string;
  company_city?: string;
  company_street_address?: string;
  company_address_complete?: string;
  company_created_at?: string;
  company_updated_at?: string;
  custom_field_values?: Record<number, string>;
  company_custom_field_values?: Record<number, string>;
}

/* Une ligne de l'export (09/10/2026) : un contact et son entreprise, ou une
   entreprise seule quand on exporte les entreprises. L'aperçu et le fichier
   se construisent à partir de la même ligne, donc montrent la même chose. */
interface LigneExport {
  cle: string;
  contact: ExportableContact | null;
  entreprise: Record<string, unknown>;
  champsContact: Record<number, string>;
  champsEntreprise: Record<number, string>;
}

/** Un filtre est complet quand il a une valeur, ou n'en demande pas. */
const filtreComplet = (f: { field: string; operator: string; value: string }) =>
  Boolean(f.field && f.operator) &&
  (["is_empty", "is_not_empty"].includes(f.operator) ||
    (Boolean(f.value) && f.value !== "[]" && f.value !== "|"));

const valeursMultiples = (valeur: string): string[] => {
  try {
    const lu = JSON.parse(valeur || "[]");
    return Array.isArray(lu) ? lu.map(String) : [];
  } catch {
    return [];
  }
};

interface ExportConfiguration {
  contactFields: {
    standard: string[];
    custom: number[];
  };
  companyFields: {
    standard: string[];
    custom: number[];
  };
  contactFilters: {
    field: string;
    operator: string;
    value: string;
  }[];
  companyFilters: {
    field: string;
    operator: string;
    value: string;
  }[];
  filterLogicOperator: "AND" | "OR";
}

export default function Export() {
  const { toast } = useToast();
  const { token } = useAuthStore();

  // États pour les champs disponibles
  const [contactCustomFields, setContactCustomFields] = useState<
    (CustomField & { usage_count: number; options?: CustomFieldOption[] })[]
  >([]);
  const [companyCustomFields, setCompanyCustomFields] = useState<
    (CompanyCustomField & {
      usage_count: number;
      options?: CompanyCustomFieldOption[];
    })[]
  >([]);

  // États pour les sections collapsibles
  const [isContactFieldsOpen, setIsContactFieldsOpen] = useState(false);
  const [isCompanyFieldsOpen, setIsCompanyFieldsOpen] = useState(false);
  const [isContactFiltersOpen, setIsContactFiltersOpen] = useState(false);
  const [isCompanyFiltersOpen, setIsCompanyFiltersOpen] = useState(false);
  const [isSavedConfigsOpen, setIsSavedConfigsOpen] = useState(false);

  // Onglet entité actif dans le constructeur d'export (Proposition A)
  const [activeExportTab, setActiveExportTab] = useState<"contact" | "company">(
    "contact",
  );

  // États pour la configuration d'export
  const [exportConfig, setExportConfig] = useState<ExportConfiguration>({
    contactFields: {
      standard: ["first_name", "last_name", "email"], // Champs par défaut cochés
      custom: [],
    },
    companyFields: {
      standard: ["name"], // Champ par défaut coché
      custom: [],
    },
    contactFilters: [],
    companyFilters: [],
    filterLogicOperator: "AND",
  });

  // États pour l'aperçu des résultats
  const [previewData, setPreviewData] = useState<LigneExport[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState(0);

  // États pour l'export
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // États pour les configurations sauvegardées
  const [savedConfigs, setSavedConfigs] = useState<
    Array<{ id: number; name: string; description?: string }>
  >([]);
  const [configSearch, setConfigSearch] = useState("");
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [isApplyingConfig, setIsApplyingConfig] = useState(false);
  const [isEditingConfig, setIsEditingConfig] = useState<number | null>(null);
  const [isDuplicatingConfig, setIsDuplicatingConfig] = useState<number | null>(
    null,
  );
  const [isDeletingConfig, setIsDeletingConfig] = useState<number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // États pour la modale d'aperçu
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewConfigData, setPreviewConfigData] = useState<{
    name: string;
    contactFilters: any[];
    companyFilters: any[];
  } | null>(null);

  // Champs standards pour les contacts
  const contactStandardFields = [
    { id: "id", label: "ID", fieldType: "number" as const },
    { id: "first_name", label: "Prénom", fieldType: "text" as const },
    { id: "last_name", label: "Nom", fieldType: "text" as const },
    { id: "email", label: "Email", fieldType: "email" as const },
    { id: "phone", label: "Téléphone", fieldType: "text" as const },
    { id: "company_id", label: "ID Entreprise", fieldType: "number" as const },
    { id: "created_at", label: "Date de création", fieldType: "date" as const },
    {
      id: "updated_at",
      label: "Date de modification",
      fieldType: "date" as const,
    },
  ];

  // Champs standards pour les entreprises
  const companyStandardFields = [
    { id: "id", label: "ID", fieldType: "number" as const },
    { id: "name", label: "Nom", fieldType: "text" as const },
    { id: "website", label: "Site web", fieldType: "url" as const },
    { id: "postal_code", label: "Code postal", fieldType: "text" as const },
    { id: "city", label: "Ville", fieldType: "text" as const },
    { id: "street_address", label: "Adresse rue", fieldType: "text" as const },
    {
      id: "address_complete",
      label: "Adresse complète",
      fieldType: "text" as const,
    },
    { id: "created_at", label: "Date de création", fieldType: "date" as const },
    {
      id: "updated_at",
      label: "Date de modification",
      fieldType: "date" as const,
    },
  ];



  // Fonction pour charger les configurations sauvegardées
  const loadSavedConfigurations = async () => {
    setIsLoadingConfigs(true);
    try {
      const response = await fetch("/api/filter-sets?page=1&limit=100", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSavedConfigs(result.data.data || []);
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement des configurations:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les configurations sauvegardées",
        variant: "destructive",
      });
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  // Fonction pour appliquer une configuration
  const applyConfiguration = async (configId: number) => {
    setIsApplyingConfig(true);
    try {
      const response = await fetch(`/api/filter-sets/${configId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Récupérer les filter_rules pour cette configuration
          const rulesResponse = await fetch(
            `/api/filter-sets/${configId}/rules`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (rulesResponse.ok) {
            const rulesResult = await rulesResponse.json();
            if (rulesResult.success && rulesResult.data) {
              // Séparer les filtres par scope
              const contactFilters = rulesResult.data
                .filter((rule: any) => rule.scope === "contacts")
                .map((rule: any) => ({
                  field: rule.field_name,
                  operator: rule.operator,
                  value: rule.value || "",
                }));

              const companyFilters = rulesResult.data
                .filter((rule: any) => rule.scope === "companies")
                .map((rule: any) => ({
                  field: rule.field_name,
                  operator: rule.operator,
                  value: rule.value || "",
                }));

              // Appliquer la configuration
              setExportConfig((prev) => ({
                ...prev,
                contactFilters,
                companyFilters,
              }));

              // Ouvrir automatiquement les sections de filtres pour voir les filtres appliqués
              setIsContactFiltersOpen(true);
              setIsCompanyFiltersOpen(true);

              toast({
                title: "Configuration appliquée",
                description: `La configuration "${result.data.name}" a été appliquée avec succès`,
              });
            }
          } else {
            console.error(
              "❌ Erreur lors de la récupération des règles:",
              rulesResponse.status,
            );
          }
        }
      } else {
        console.error(
          "❌ Erreur lors de la récupération de la configuration:",
          response.status,
        );
      }
    } catch (error) {
      console.error(
        "❌ Erreur lors de l'application de la configuration:",
        error,
      );
      toast({
        title: "Erreur",
        description: "Impossible d'appliquer la configuration",
        variant: "destructive",
      });
    } finally {
      setIsApplyingConfig(false);
    }
  };

  // Fonction pour modifier une configuration (charger ses données dans le formulaire)
  const editConfiguration = async (configId: number) => {
    setIsEditingConfig(configId);
    try {
      // Charger la configuration et ses règles
      const [configResponse, rulesResponse] = await Promise.all([
        fetch(`/api/filter-sets/${configId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/filter-sets/${configId}/rules`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (configResponse.ok && rulesResponse.ok) {
        const configResult = await configResponse.json();
        const rulesResult = await rulesResponse.json();

        if (configResult.success && rulesResult.success) {
          // Charger la configuration dans le formulaire
          const contactFilters = rulesResult.data
            .filter((rule: any) => rule.scope === "contacts")
            .map((rule: any) => ({
              field: rule.field_name,
              operator: rule.operator,
              value: rule.value || "",
            }));

          const companyFilters = rulesResult.data
            .filter((rule: any) => rule.scope === "companies")
            .map((rule: any) => ({
              field: rule.field_name,
              operator: rule.operator,
              value: rule.value || "",
            }));

          setExportConfig((prev) => ({
            ...prev,
            contactFilters,
            companyFilters,
          }));

          // Ouvrir les sections pour voir les filtres chargés
          setIsContactFiltersOpen(true);
          setIsCompanyFiltersOpen(true);

          toast({
            title: "Configuration chargée",
            description: `La configuration "${configResult.data.name}" a été chargée pour modification`,
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la configuration:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger la configuration",
        variant: "destructive",
      });
    } finally {
      setIsEditingConfig(null);
    }
  };

  // Fonction pour dupliquer une configuration
  const duplicateConfiguration = async (configId: number) => {
    setIsDuplicatingConfig(configId);
    try {
      // Charger la configuration et ses règles
      const [configResponse, rulesResponse] = await Promise.all([
        fetch(`/api/filter-sets/${configId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/filter-sets/${configId}/rules`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (configResponse.ok && rulesResponse.ok) {
        const configResult = await configResponse.json();
        const rulesResult = await rulesResponse.json();

        if (configResult.success && rulesResult.success) {
          // Charger la configuration dans le formulaire
          const contactFilters = rulesResult.data
            .filter((rule: any) => rule.scope === "contacts")
            .map((rule: any) => ({
              field: rule.field_name,
              operator: rule.operator,
              value: rule.value || "",
            }));

          const companyFilters = rulesResult.data
            .filter((rule: any) => rule.scope === "companies")
            .map((rule: any) => ({
              field: rule.field_name,
              operator: rule.operator,
              value: rule.value || "",
            }));

          setExportConfig((prev) => ({
            ...prev,
            contactFilters,
            companyFilters,
          }));

          // Ouvrir les sections pour voir les filtres chargés
          setIsContactFiltersOpen(true);
          setIsCompanyFiltersOpen(true);

          toast({
            title: "Configuration dupliquée",
            description: `La configuration "${configResult.data.name}" a été dupliquée. Vous pouvez maintenant la modifier et la sauvegarder sous un nouveau nom.`,
          });
        }
      }
    } catch (error) {
      console.error(
        "Erreur lors de la duplication de la configuration:",
        error,
      );
      toast({
        title: "Erreur",
        description: "Impossible de dupliquer la configuration",
        variant: "destructive",
      });
    } finally {
      setIsDuplicatingConfig(null);
    }
  };

  // Fonction pour ouvrir la modale de suppression
  const deleteConfiguration = (configId: number, configName: string) => {
    setConfigToDelete({ id: configId, name: configName });
    setDeleteModalOpen(true);
  };

  // Fonction pour confirmer la suppression
  const confirmDeleteConfiguration = async () => {
    if (!configToDelete) return;

    setIsDeletingConfig(configToDelete.id);
    try {
      const response = await fetch(`/api/filter-sets/${configToDelete.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        // Recharger la liste des configurations
        await loadSavedConfigurations();
        toast({
          title: "Configuration supprimée",
          description: `La configuration "${configToDelete.name}" a été supprimée avec succès`,
        });
        setDeleteModalOpen(false);
        setConfigToDelete(null);
      } else {
        throw new Error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error(
        "Erreur lors de la suppression de la configuration:",
        error,
      );
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la configuration",
        variant: "destructive",
      });
    } finally {
      setIsDeletingConfig(null);
    }
  };

  // Fonction pour afficher l'aperçu d'une configuration
  const previewConfiguration = async (configId: number) => {
    try {
      const [configResponse, rulesResponse] = await Promise.all([
        fetch(`/api/filter-sets/${configId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/filter-sets/${configId}/rules`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (configResponse.ok && rulesResponse.ok) {
        const configResult = await configResponse.json();
        const rulesResult = await rulesResponse.json();

        if (configResult.success && rulesResult.success) {
          const contactFilters = rulesResult.data.filter(
            (rule: any) => rule.scope === "contacts",
          );
          const companyFilters = rulesResult.data.filter(
            (rule: any) => rule.scope === "companies",
          );

          setPreviewConfigData({
            name: configResult.data.name,
            contactFilters,
            companyFilters,
          });
          setIsPreviewModalOpen(true);
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'aperçu de la configuration:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'afficher l'aperçu de la configuration",
        variant: "destructive",
      });
    }
  };

  // Filtrer les configurations selon la recherche
  const filteredConfigs = savedConfigs.filter(
    (config) =>
      config.name.toLowerCase().includes(configSearch.toLowerCase()) ||
      (config.description &&
        config.description.toLowerCase().includes(configSearch.toLowerCase())),
  );
  /* Les opérateurs proposés selon le type du champ. Tous sont compris par
     le serveur (09/10/2026) : auparavant, ceux des nombres et des dates
     étaient proposés puis ignorés. */
  const getFilterOperators = (fieldType: string) => {
    switch (fieldType) {
      case "number":
        return [
          { value: "equals", label: "Est égal à" },
          { value: "not_equals", label: "N'est pas égal à" },
          { value: "greater_than", label: "Supérieur à" },
          { value: "less_than", label: "Inférieur à" },
          { value: "greater_than_or_equal", label: "Supérieur ou égal à" },
          { value: "less_than_or_equal", label: "Inférieur ou égal à" },
          { value: "is_empty", label: "Est vide" },
          { value: "is_not_empty", label: "N'est pas vide" },
        ];
      case "date":
        return [
          { value: "equals", label: "Est le" },
          { value: "before", label: "Avant le" },
          { value: "after", label: "Après le" },
          { value: "between", label: "Entre" },
          { value: "is_empty", label: "Est vide" },
          { value: "is_not_empty", label: "N'est pas vide" },
        ];
      case "radio":
      case "select":
        return [
          { value: "in", label: "Est l'une de" },
          { value: "not_in", label: "N'est aucune de" },
          { value: "is_empty", label: "Est vide" },
          { value: "is_not_empty", label: "N'est pas vide" },
        ];
      case "checkbox":
        return [
          { value: "contains_option", label: "Contient l'option" },
          { value: "not_contains_option", label: "Ne contient pas l'option" },
          { value: "is_empty", label: "Est vide" },
          { value: "is_not_empty", label: "N'est pas vide" },
        ];
      case "text":
      case "email":
      case "url":
      default:
        return [
          { value: "equals", label: "Est égal à" },
          { value: "not_equals", label: "N'est pas égal à" },
          { value: "contains", label: "Contient" },
          { value: "starts_with", label: "Commence par" },
          { value: "ends_with", label: "Se termine par" },
          { value: "is_empty", label: "Est vide" },
          { value: "is_not_empty", label: "N'est pas vide" },
        ];
    }
  };

  // Charger les champs personnalisés au montage
  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const [contactFieldsResponse, companyFieldsResponse] =
          await Promise.all([
            apiService.getContactCustomFields(),
            apiService.getCompanyCustomFieldsWithUsage(),
          ]);

        if (contactFieldsResponse.success && contactFieldsResponse.data) {
          setContactCustomFields(contactFieldsResponse.data);
        }

        if (companyFieldsResponse.success && companyFieldsResponse.data) {
          setCompanyCustomFields(companyFieldsResponse.data);
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des champs personnalisés:",
          error,
        );
        toast({
          title: "Erreur",
          description: "Impossible de charger les champs personnalisés",
          variant: "destructive",
        });
      }
    };

    loadCustomFields();
    loadSavedConfigurations();
  }, [toast]);

  // Charger l'aperçu des données quand la configuration ou l'onglet change
  useEffect(() => {
    loadPreviewData();
  }, [exportConfig, activeExportTab]);

  const dateFr = (valeur: unknown) => {
    if (!valeur) return "";
    try {
      return new Date(String(valeur)).toLocaleString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(valeur);
    }
  };

  /* Les lignes de l'export, selon l'onglet : un contact et son entreprise,
     ou une entreprise seule (y compris sans aucun contact). */
  const versLignes = (donnees: any[]): LigneExport[] =>
    activeExportTab === "contact"
      ? donnees.map((c) => ({
          cle: `c${c.id}`,
          contact: c,
          entreprise: {
            id: c.company_id,
            name: c.company_name,
            website: c.company_website,
            postal_code: c.company_postal_code,
            city: c.company_city,
            street_address: c.company_street_address,
            address_complete: c.company_address_complete,
            created_at: c.company_created_at,
            updated_at: c.company_updated_at,
          },
          champsContact: c.custom_field_values || {},
          champsEntreprise: c.company_custom_field_values || {},
        }))
      : donnees.map((e) => ({
          cle: `e${e.id}`,
          contact: null,
          entreprise: { ...e, address_complete: e.address_complement },
          champsContact: {},
          champsEntreprise: e.custom_field_values || {},
        }));

  /* Les colonnes, dans l'ordre du fichier. Le titre d'aperçu dit l'origine
     de la colonne ; le titre du fichier garde les noms compatibles avec
     l'import. */
  const colonnesExport = () => {
    const colonnes: { cle: string; titre: string; titreFichier: string; valeur: (l: LigneExport) => string }[] = [];
    if (activeExportTab === "contact") {
      exportConfig.contactFields.standard.forEach((id) => {
        const champ = contactStandardFields.find((f) => f.id === id);
        if (!champ) return;
        colonnes.push({
          cle: `contact_${id}`,
          titre: `CONTACT - ${champ.label.toUpperCase()}`,
          titreFichier: getImportFieldName(id, "contact"),
          valeur: (l) => {
            const v = (l.contact as any)?.[id];
            return id === "created_at" || id === "updated_at" ? dateFr(v) : v == null ? "" : String(v);
          },
        });
      });
      exportConfig.contactFields.custom.forEach((id) => {
        const champ = contactCustomFields.find((f) => f.id === id);
        if (!champ) return;
        colonnes.push({
          cle: `contact_custom_${id}`,
          titre: `CONTACT - ${champ.name.toUpperCase()}`,
          titreFichier: champ.name,
          valeur: (l) => l.champsContact[id] || "",
        });
      });
    }
    exportConfig.companyFields.standard.forEach((id) => {
      const champ = companyStandardFields.find((f) => f.id === id);
      if (!champ) return;
      colonnes.push({
        cle: `company_${id}`,
        titre: `ENTREPRISE - ${champ.label.toUpperCase()}`,
        titreFichier: getImportFieldName(id, "company"),
        valeur: (l) => {
          const v = l.entreprise[id];
          return id === "created_at" || id === "updated_at" ? dateFr(v) : v == null ? "" : String(v);
        },
      });
    });
    exportConfig.companyFields.custom.forEach((id) => {
      const champ = companyCustomFields.find((f) => f.id === id);
      if (!champ) return;
      colonnes.push({
        cle: `company_custom_${id}`,
        titre: `ENTREPRISE - ${champ.name.toUpperCase()}`,
        titreFichier: champ.name,
        valeur: (l) => l.champsEntreprise[id] || "",
      });
    });
    return colonnes;
  };

  /* Les données filtrées par le serveur. Sur l'onglet Entreprises, seuls
     les filtres d'entreprise s'appliquent. */
  const chargerLignes = async (limit: number) => {
    const filtres =
      activeExportTab === "contact"
        ? [...exportConfig.contactFilters, ...exportConfig.companyFilters].filter(filtreComplet)
        : exportConfig.companyFilters.filter(filtreComplet);
    const filters = filtres.length > 0 ? { filters: filtres, logicOperator: exportConfig.filterLogicOperator } : undefined;
    const reponse =
      activeExportTab === "contact"
        ? await apiService.getContacts({ page: 1, limit, filters })
        : await apiService.getCompanies({ page: 1, limit, filters, sort: "name", order: "asc" });
    if (!reponse.success || !reponse.data) {
      throw new Error(reponse.error || "Impossible de récupérer les données");
    }
    return { lignes: versLignes(reponse.data.data as any[]), total: reponse.data.total };
  };

  // Fonction pour charger l'aperçu des données
  const loadPreviewData = async () => {
    setPreviewLoading(true);
    try {
      const { lignes, total } = await chargerLignes(100);
      setPreviewData(lignes);
      setPreviewCount(total);
    } catch (error) {
      console.error("Erreur lors du chargement de l'aperçu:", error);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fonction pour basculer un champ standard
  const toggleStandardField = (
    type: "contact" | "company",
    fieldId: string,
  ) => {
    setExportConfig((prev) => {
      if (type === "contact") {
        return {
          ...prev,
          contactFields: {
            ...prev.contactFields,
            standard: prev.contactFields.standard.includes(fieldId)
              ? prev.contactFields.standard.filter((id) => id !== fieldId)
              : [...prev.contactFields.standard, fieldId],
          },
        };
      } else {
        return {
          ...prev,
          companyFields: {
            ...prev.companyFields,
            standard: prev.companyFields.standard.includes(fieldId)
              ? prev.companyFields.standard.filter((id) => id !== fieldId)
              : [...prev.companyFields.standard, fieldId],
          },
        };
      }
    });
  };

  // Fonction pour basculer un champ personnalisé
  const toggleCustomField = (type: "contact" | "company", fieldId: number) => {
    setExportConfig((prev) => {
      if (type === "contact") {
        return {
          ...prev,
          contactFields: {
            ...prev.contactFields,
            custom: prev.contactFields.custom.includes(fieldId)
              ? prev.contactFields.custom.filter((id) => id !== fieldId)
              : [...prev.contactFields.custom, fieldId],
          },
        };
      } else {
        return {
          ...prev,
          companyFields: {
            ...prev.companyFields,
            custom: prev.companyFields.custom.includes(fieldId)
              ? prev.companyFields.custom.filter((id) => id !== fieldId)
              : [...prev.companyFields.custom, fieldId],
          },
        };
      }
    });
  };

  // ========== FONCTIONS DE GESTION DES FILTRES CONTACTS ==========

  const addContactAdvancedFilter = () => {
    const availableFields = getFilterFieldOptions("contact");
    // Premier champ proposé, avec la première condition de son type.
    const premier = availableFields[0];
    const defaultField = premier ? premier.value : "first_name";
    const defaultOperator =
      getFilterOperators(premier?.fieldType || "text")[0]?.value || "contains";
    setExportConfig((prev) => ({
      ...prev,
      contactFilters: [
        ...prev.contactFilters,
        { field: defaultField, operator: defaultOperator, value: "" },
      ],
    }));
  };

  const updateContactAdvancedFilter = (
    index: number,
    field: "field" | "operator" | "value",
    value: string,
  ) => {
    setExportConfig((prev) => ({
      ...prev,
      contactFilters: prev.contactFilters.map((filter, i) => {
        if (i === index) {
          const updatedFilter = { ...filter, [field]: value };

          // Si on change le champ, réinitialiser l'opérateur et la valeur
          if (field === "field") {
            // Déterminer le type de champ
            let fieldType = "text";

            // Chercher dans les champs standards de contact
            const standardField = contactStandardFields.find(
              (f) => f.id === value,
            );
            if (standardField) {
              fieldType = standardField.fieldType;
            } else {
              // Chercher dans les champs personnalisés de contact
              const customFieldId = value.startsWith("custom_")
                ? parseInt(value.replace("custom_", ""))
                : null;
              if (customFieldId) {
                const customField = contactCustomFields.find(
                  (f) => f.id === customFieldId,
                );
                if (customField) {
                  fieldType = customField.type || "text";
                }
              }
            }

            // Définir l'opérateur par défaut selon le type
            const availableOperators = getFilterOperators(fieldType);
            updatedFilter.operator = availableOperators[0]?.value || "contains";
            updatedFilter.value = "";
          }

          // Changer d'opérateur change la forme de la valeur : on repart à vide.
          if (field === "operator" && value !== filter.operator) {
            updatedFilter.value = "";
          }

          return updatedFilter;
        }
        return filter;
      }),
    }));
  };

  const removeContactAdvancedFilter = (index: number) => {
    setExportConfig((prev) => ({
      ...prev,
      contactFilters: prev.contactFilters.filter((_, i) => i !== index),
    }));
  };

  const resetContactAdvancedFilters = () => {
    setExportConfig((prev) => ({
      ...prev,
      contactFilters: [],
    }));
  };

  // ========== FONCTIONS DE GESTION DES FILTRES ENTREPRISES ==========

  const addCompanyAdvancedFilter = () => {
    const availableFields = getFilterFieldOptions("company");
    // Premier champ proposé, avec la première condition de son type.
    const premier = availableFields[0];
    const defaultField = premier ? premier.value : "company_name";
    const defaultOperator =
      getFilterOperators(premier?.fieldType || "text")[0]?.value || "contains";
    setExportConfig((prev) => ({
      ...prev,
      companyFilters: [
        ...prev.companyFilters,
        { field: defaultField, operator: defaultOperator, value: "" },
      ],
    }));
  };

  const updateCompanyAdvancedFilter = (
    index: number,
    field: "field" | "operator" | "value",
    value: string,
  ) => {
    setExportConfig((prev) => ({
      ...prev,
      companyFilters: prev.companyFilters.map((filter, i) => {
        if (i === index) {
          const updatedFilter = { ...filter, [field]: value };

          // Si on change le champ, réinitialiser l'opérateur et la valeur
          if (field === "field") {
            // Déterminer le type de champ
            let fieldType = "text";

            // Chercher dans les champs standards d'entreprise
            const companyFieldId = value.startsWith("company_")
              ? value.replace("company_", "")
              : value;
            const standardField = companyStandardFields.find(
              (f) => f.id === companyFieldId,
            );
            if (standardField) {
              fieldType = standardField.fieldType;
            } else {
              // Chercher dans les champs personnalisés d'entreprise
              const customFieldId = value.startsWith("company_custom_")
                ? parseInt(value.replace("company_custom_", ""))
                : null;
              if (customFieldId) {
                const customField = companyCustomFields.find(
                  (f) => f.id === customFieldId,
                );
                if (customField) {
                  fieldType = customField.type || "text";
                }
              }
            }

            // Définir l'opérateur par défaut selon le type
            const availableOperators = getFilterOperators(fieldType);
            updatedFilter.operator = availableOperators[0]?.value || "contains";
            updatedFilter.value = "";
          }

          // Changer d'opérateur change la forme de la valeur : on repart à vide.
          if (field === "operator" && value !== filter.operator) {
            updatedFilter.value = "";
          }

          return updatedFilter;
        }
        return filter;
      }),
    }));
  };

  const removeCompanyAdvancedFilter = (index: number) => {
    setExportConfig((prev) => ({
      ...prev,
      companyFilters: prev.companyFilters.filter((_, i) => i !== index),
    }));
  };

  const resetCompanyAdvancedFilters = () => {
    setExportConfig((prev) => ({
      ...prev,
      companyFilters: [],
    }));
  };

  // Fonction pour obtenir les options de champs pour les filtres
  const getFilterFieldOptions = (type: "contact" | "company") => {
    const standardOptions =
      type === "contact"
        ? contactStandardFields.map((f) => ({
            value: f.id,
            label: f.label,
            fieldType: f.fieldType,
            options: undefined,
          }))
        : companyStandardFields.map((f) => ({
            value: `company_${f.id}`,
            label: f.label,
            fieldType: f.fieldType,
            options: undefined,
          }));

    const customOptions =
      type === "contact"
        ? contactCustomFields.map((f) => ({
            value: `custom_${f.id}`,
            label: f.name,
            fieldType: f.type || "text",
            options: f.options,
          }))
        : companyCustomFields.map((f) => ({
            value: `company_custom_${f.id}`,
            label: f.name,
            fieldType: f.type || "text",
            options: f.options,
          }));

    return [...standardOptions, ...customOptions];
  };

  // Fonction pour rendre le champ de valeur selon le type de champ et l'opérateur
  const renderValueField = (
    filter: { field: string; operator: string; value: string },
    index: number,
    type: "contact" | "company",
  ) => {
    // Si l'opérateur ne nécessite pas de valeur
    if (["is_empty", "is_not_empty"].includes(filter.operator)) {
      return (
        <div className="h-10 flex items-center px-3 py-2 border border-gray-200 bg-gray-50 rounded-md">
          <span className="text-sm text-gray-500">Aucune valeur requise</span>
        </div>
      );
    }

    // Déterminer le type de champ et les options
    const availableFields = getFilterFieldOptions(type);
    const selectedField = availableFields.find((f) => f.value === filter.field);
    const fieldType = selectedField?.fieldType || "text";
    const fieldOptions = selectedField?.options;

    const updateFunction =
      type === "contact"
        ? updateContactAdvancedFilter
        : updateCompanyAdvancedFilter;

    switch (fieldType) {
      case "number":
        return (
          <Input
            type="number"
            placeholder="Nombre"
            value={filter.value}
            onChange={(e) => updateFunction(index, "value", e.target.value)}
          />
        );

      case "date":
        if (filter.operator === "between") {
          const [startDate, endDate] = filter.value.split("|");
          return (
            <div className="flex space-x-2">
              <Input
                type="date"
                placeholder="Date de début"
                value={startDate || ""}
                onChange={(e) => {
                  const newValue = `${e.target.value}|${endDate || ""}`;
                  updateFunction(index, "value", newValue);
                }}
              />
              <Input
                type="date"
                placeholder="Date de fin"
                value={endDate || ""}
                onChange={(e) => {
                  const newValue = `${startDate || ""}|${e.target.value}`;
                  updateFunction(index, "value", newValue);
                }}
              />
            </div>
          );
        }
        return (
          <Input
            type="date"
            placeholder="Date"
            value={filter.value}
            onChange={(e) => updateFunction(index, "value", e.target.value)}
          />
        );

      case "radio":
      case "select":
      case "checkbox": {
        if (!fieldOptions || fieldOptions.length === 0) {
          return (
            <Input
              placeholder="Valeur"
              value={filter.value}
              onChange={(e) => updateFunction(index, "value", e.target.value)}
            />
          );
        }
        // Une case à cocher multiple (ou une configuration enregistrée avant
        // le 09/10/2026, en « Est égal à ») : une seule option recherchée.
        if (["contains_option", "not_contains_option", "equals", "not_equals"].includes(filter.operator)) {
          return (
            <Select
              value={filter.value}
              onValueChange={(value) => updateFunction(index, "value", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisir une option..." />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions.map((option) => (
                  <SelectItem key={option.id} value={option.value || ""}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        // Une liste : on coche une ou plusieurs valeurs (« Est l'une de »).
        const cochees = valeursMultiples(filter.value);
        return (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 rounded-md border border-gray-200 bg-white px-3 py-2">
            {fieldOptions.map((option) => {
              const v = option.value || "";
              const coche = cochees.includes(v);
              return (
                <label key={option.id} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-blue-600"
                    checked={coche}
                    onChange={() => {
                      const suivantes = coche ? cochees.filter((x) => x !== v) : [...cochees, v];
                      updateFunction(index, "value", suivantes.length ? JSON.stringify(suivantes) : "");
                    }}
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
        );
      }

      case "email":
        return (
          <Input
            type="email"
            placeholder="email@exemple.com"
            value={filter.value}
            onChange={(e) => updateFunction(index, "value", e.target.value)}
          />
        );

      case "url":
        return (
          <Input
            type="url"
            placeholder="https://exemple.com"
            value={filter.value}
            onChange={(e) => updateFunction(index, "value", e.target.value)}
          />
        );

      case "text":
      default:
        return (
          <Input
            placeholder="Valeur"
            value={filter.value}
            onChange={(e) => updateFunction(index, "value", e.target.value)}
          />
        );
    }
  };

  // Fonction pour ouvrir la modal de sauvegarde
  const saveConfiguration = () => {
    // Vérifier qu'il y a au moins un filtre
    const hasContactFilters = exportConfig.contactFilters.length > 0;
    const hasCompanyFilters = exportConfig.companyFilters.length > 0;

    if (!hasContactFilters && !hasCompanyFilters) {
      toast({
        title: "Aucun filtre défini",
        description:
          "Vous devez ajouter au moins un filtre de contact ou d'entreprise pour pouvoir sauvegarder cette configuration.",
        variant: "destructive",
      });
      return;
    }

    setIsModalOpen(true);
  };

  // Fonction pour sauvegarder la configuration via l'API
  const handleSaveConfiguration = async (name: string, description: string) => {
    // Log des filtres choisis par l'utilisateur
    console.log("=== CONFIGURATION D'EXPORT À SAUVEGARDER ===");
    console.log("Nom:", name);
    console.log("Description:", description);
    console.log("Configuration complète:", {
      contactFields: exportConfig.contactFields,
      companyFields: exportConfig.companyFields,
      contactFilters: exportConfig.contactFilters,
      companyFilters: exportConfig.companyFilters,
      filterLogicOperator: exportConfig.filterLogicOperator,
    });
    console.log("Filtres de contact:", exportConfig.contactFilters);
    console.log("Filtres d'entreprise:", exportConfig.companyFilters);
    console.log("Opérateur logique:", exportConfig.filterLogicOperator);
    console.log("===============================================");

    setIsSaving(true);
    try {
      // Préparer les filtres à envoyer avec leur scope
      const contactFiltersWithScope = exportConfig.contactFilters
        .filter(
          (f) =>
            f.field &&
            f.operator &&
            (f.value || ["is_empty", "is_not_empty"].includes(f.operator)),
        )
        .map((f) => ({ ...f, scope: "contacts" }));

      const companyFiltersWithScope = exportConfig.companyFilters
        .filter(
          (f) =>
            f.field &&
            f.operator &&
            (f.value || ["is_empty", "is_not_empty"].includes(f.operator)),
        )
        .map((f) => ({ ...f, scope: "companies" }));

      const allFilters = [
        ...contactFiltersWithScope,
        ...companyFiltersWithScope,
      ];

      const response = await fetch("/api/filter-sets", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          description,
          filters: allFilters,
          filterLogicOperator: exportConfig.filterLogicOperator,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Erreur lors de la sauvegarde");
      }

      toast({
        title: "Configuration sauvegardée",
        description:
          "Votre configuration d'export a été sauvegardée avec succès",
        variant: "success",
      });

      setIsModalOpen(false);
      // Recharger la liste des configurations
      loadSavedConfigurations();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      toast({
        title: "Erreur de sauvegarde",
        description:
          error instanceof Error
            ? error.message
            : "Impossible de sauvegarder la configuration",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Fonction pour mapper les noms de champs de la DB vers les noms d'import
  const getImportFieldName = (
    fieldId: string,
    type: "contact" | "company",
  ): string => {
    if (type === "contact") {
      const mapping: Record<string, string> = {
        first_name: "firstName",
        last_name: "lastName",
        email: "email",
        phone: "phone",
        id: "id",
        company_id: "company_id",
        created_at: "created_at",
        updated_at: "updated_at",
      };
      return mapping[fieldId] || fieldId;
    } else {
      const mapping: Record<string, string> = {
        name: "name",
        website: "website",
        postal_code: "postal_code",
        city: "city",
        street_address: "street_address",
        address_complete: "address_complement",
        id: "id",
        created_at: "created_at",
        updated_at: "updated_at",
      };
      return mapping[fieldId] || fieldId;
    }
  };

  // Fonction pour exporter en CSV
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const colonnes = colonnesExport();
      if (colonnes.length === 0) {
        toast({
          title: "Aucune colonne choisie",
          description: "Sélectionnez au moins un champ à exporter.",
          variant: "destructive",
        });
        return;
      }
      // Toutes les lignes, sans le plafond de 10 000 d'avant le 09/10/2026.
      const { lignes, total } = await chargerLignes(1000000);

      // Une valeur qui contient un point-virgule, un guillemet ou un retour
      // à la ligne est mise entre guillemets, sinon elle décalerait les colonnes.
      const cellule = (v: string) => (/[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
      const csvRows = [
        colonnes.map((c) => cellule(c.titreFichier)).join(";"),
        ...lignes.map((l) => colonnes.map((c) => cellule(c.valeur(l))).join(";")),
      ];

      // Le marqueur BOM permet à Excel de lire correctement les accents.
      const blob = new Blob(["\uFEFF" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const quoi = activeExportTab === "contact" ? "contacts" : "entreprises";

      link.setAttribute("href", url);
      link.setAttribute("download", `export_${quoi}_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export réussi",
        description:
          lignes.length < total
            ? `${lignes.length} ${quoi} exportés sur ${total} : relancez l'export pour obtenir le reste.`
            : `${lignes.length} ${quoi} exportés.`,
        variant: "success",
      });
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      toast({
        title: "Erreur d'export",
        description: "Impossible d'exporter les données",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Download className="w-6 h-6 text-blue-600" />
            <span>Exportation</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Exportez vos données en CSV
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={saveConfiguration}
            disabled={isSaving}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? "Sauvegarde..." : "Enregistrer cette configuration"}
          </Button>
          <Button
            onClick={exportToCSV}
            disabled={isExporting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? "Export..." : "Exporter en CSV"}
          </Button>
        </div>
      </div>

      {/* Section Configurations sauvegardées */}
      <Card>
        <Collapsible
          open={isSavedConfigsOpen}
          onOpenChange={setIsSavedConfigsOpen}
        >
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Configurations sauvegardées</CardTitle>
                  <CardDescription>
                    Appliquez une configuration de filtres précédemment
                    sauvegardée
                  </CardDescription>
                </div>
                {isSavedConfigsOpen ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              {/* Champ de recherche */}
              <div className="relative">
                <Input
                  placeholder="Rechercher une configuration..."
                  value={configSearch}
                  onChange={(e) => setConfigSearch(e.target.value)}
                  className="pr-10"
                />
                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              {/* Liste des configurations */}
              {isLoadingConfigs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600">
                    Chargement des configurations...
                  </span>
                </div>
              ) : filteredConfigs.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {filteredConfigs.map((config) => (
                    <div
                      key={config.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{config.name}</h4>
                        {config.description && (
                          <p className="text-xs text-gray-600 mt-1">
                            {config.description}
                          </p>
                        )}
                      </div>

                      {/* Menu d'actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="ml-3">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-48">
                          <DropdownMenuItem
                            onClick={() => applyConfiguration(config.id)}
                            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
                            disabled={isApplyingConfig}
                          >
                            {isApplyingConfig ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                            <span>Appliquer</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => editConfiguration(config.id)}
                            className="flex items-center space-x-2"
                            disabled={isEditingConfig === config.id}
                          >
                            {isEditingConfig === config.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Edit className="w-4 h-4" />
                            )}
                            <span>Modifier</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => duplicateConfiguration(config.id)}
                            className="flex items-center space-x-2"
                            disabled={isDuplicatingConfig === config.id}
                          >
                            {isDuplicatingConfig === config.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                            <span>Dupliquer</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => previewConfiguration(config.id)}
                            className="flex items-center space-x-2"
                            disabled={previewLoading}
                          >
                            {previewLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                            <span>Aperçu</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() =>
                              deleteConfiguration(config.id, config.name)
                            }
                            className="flex items-center space-x-2 text-red-600 hover:text-red-700"
                            disabled={isDeletingConfig === config.id}
                          >
                            {isDeletingConfig === config.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            <span>Supprimer</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  {configSearch
                    ? "Aucune configuration trouvée"
                    : "Aucune configuration sauvegardée"}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Constructeur d'export : configuration (onglets) à gauche + aperçu permanent à droite */}
      <div className="space-y-6">
          {/* Onglets par entité */}
          <div className="inline-flex p-1 rounded-xl bg-gray-100">
            <button
              type="button"
              onClick={() => setActiveExportTab("contact")}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${
                activeExportTab === "contact"
                  ? "bg-white shadow-sm text-blue-700"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Users className="w-4 h-4" /> Contacts
            </button>
            <button
              type="button"
              onClick={() => setActiveExportTab("company")}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${
                activeExportTab === "company"
                  ? "bg-white shadow-sm text-blue-700"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Building2 className="w-4 h-4" /> Entreprises
            </button>
          </div>
          <p className="text-sm text-gray-600">
            {activeExportTab === "contact"
              ? "Une ligne par contact. Les champs et les filtres d'entreprise portent sur l'entreprise de chaque contact."
              : "Une ligne par entreprise, y compris celles qui n'ont aucun contact. Seuls les champs et les filtres d'entreprise s'appliquent."}
          </p>

          {/* Section Champs de contact */}
          {activeExportTab === "contact" && (
          <Card>
            <CardHeader>
              <CardTitle>Champs de contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Sélectionnez les champs de contact à inclure dans l'exportation
              </p>

              {/* Champs standards */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Champs standards
                </h4>
                <div className="flex flex-wrap gap-2">
                  {contactStandardFields.map((field) => {
                    const active =
                      exportConfig.contactFields.standard.includes(field.id);
                    return (
                      <button
                        key={field.id}
                        type="button"
                        onClick={() => toggleStandardField("contact", field.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1 transition-colors ${
                          active
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {active && <Check className="w-3.5 h-3.5" />}
                        {field.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Champs personnalisés */}
              {contactCustomFields.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Champs personnalisés
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {contactCustomFields.map((field) => {
                      const active =
                        exportConfig.contactFields.custom.includes(field.id);
                      return (
                        <button
                          key={field.id}
                          type="button"
                          onClick={() => toggleCustomField("contact", field.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1 transition-colors ${
                            active
                              ? "bg-blue-600 text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {active && <Check className="w-3.5 h-3.5" />}
                          {field.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Section Champs d'entreprise : sur les deux onglets (les colonnes
              de l'entreprise du contact, ou celles de l'entreprise exportée). */}
          {(
          <Card>
            <CardHeader>
              <CardTitle>Champs d'entreprise</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Sélectionnez les champs d'entreprise à inclure dans
                l'exportation
              </p>

              {/* Champs standards */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Champs standards
                </h4>
                <div className="flex flex-wrap gap-2">
                  {companyStandardFields.map((field) => {
                    const active =
                      exportConfig.companyFields.standard.includes(field.id);
                    return (
                      <button
                        key={field.id}
                        type="button"
                        onClick={() => toggleStandardField("company", field.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1 transition-colors ${
                          active
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {active && <Check className="w-3.5 h-3.5" />}
                        {field.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Champs personnalisés */}
              {companyCustomFields.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Champs personnalisés
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {companyCustomFields.map((field) => {
                      const active =
                        exportConfig.companyFields.custom.includes(field.id);
                      return (
                        <button
                          key={field.id}
                          type="button"
                          onClick={() => toggleCustomField("company", field.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1 transition-colors ${
                            active
                              ? "bg-blue-600 text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {active && <Check className="w-3.5 h-3.5" />}
                          {field.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Section Filtres de contact */}
          {activeExportTab === "contact" && (
          <Card>
            <CardHeader>
              <CardTitle>Filtres de contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtres avancés - contenu affiché directement */}
              <div className="mt-4">
                <div className="space-y-4">
                  {/* Grille des filtres */}
                  {exportConfig.contactFilters.map((filter, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 gap-3 items-end"
                    >
                      {/* Champ */}
                      <div className="col-span-4">
                        <Label className="text-sm font-medium text-gray-700">
                          Champ
                        </Label>
                        <Select
                          value={filter.field}
                          onValueChange={(value) =>
                            updateContactAdvancedFilter(index, "field", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Champs standards */}
                            <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100">
                              Champs standards
                            </div>
                            {getFilterFieldOptions("contact")
                              .filter(
                                (field) => !field.value.includes("custom_"),
                              )
                              .map((field) => (
                                <SelectItem
                                  key={field.value}
                                  value={field.value}
                                >
                                  {field.label}
                                </SelectItem>
                              ))}

                            {/* Champs personnalisés */}
                            {getFilterFieldOptions("contact").some((field) =>
                              field.value.includes("custom_"),
                            ) && (
                              <>
                                <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100 mt-1">
                                  Champs personnalisés
                                </div>
                                {getFilterFieldOptions("contact")
                                  .filter((field) =>
                                    field.value.includes("custom_"),
                                  )
                                  .map((field) => (
                                    <SelectItem
                                      key={field.value}
                                      value={field.value}
                                    >
                                      {field.label}
                                    </SelectItem>
                                  ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Opérateur */}
                      <div className="col-span-3">
                        <Label className="text-sm font-medium text-gray-700">
                          Condition
                        </Label>
                        <Select
                          value={filter.operator}
                          onValueChange={(value) =>
                            updateContactAdvancedFilter(
                              index,
                              "operator",
                              value,
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getFilterOperators(
                              getFilterFieldOptions("contact").find(
                                (f) => f.value === filter.field,
                              )?.fieldType || "text",
                            ).map((operator) => (
                              <SelectItem
                                key={operator.value}
                                value={operator.value}
                              >
                                {operator.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Valeur */}
                      <div className="col-span-4">
                        <Label className="text-sm font-medium text-gray-700">
                          Valeur
                        </Label>
                        {renderValueField(filter, index, "contact")}
                      </div>

                      {/* Bouton supprimer */}
                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeContactAdvancedFilter(index)}
                          className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Opérateur logique entre les filtres */}
                  {exportConfig.contactFilters.length > 1 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Opérateur logique entre les filtres
                      </Label>
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="contact-filter-and"
                            name="contactFilterLogic"
                            value="AND"
                            checked={exportConfig.filterLogicOperator === "AND"}
                            onChange={(e) =>
                              setExportConfig((prev) => ({
                                ...prev,
                                filterLogicOperator: e.target.value as
                                  | "AND"
                                  | "OR",
                              }))
                            }
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <label
                            htmlFor="contact-filter-and"
                            className="text-sm text-gray-700"
                          >
                            <span className="font-medium text-blue-600">
                              ET
                            </span>{" "}
                            (tous les filtres doivent correspondre)
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="contact-filter-or"
                            name="contactFilterLogic"
                            value="OR"
                            checked={exportConfig.filterLogicOperator === "OR"}
                            onChange={(e) =>
                              setExportConfig((prev) => ({
                                ...prev,
                                filterLogicOperator: e.target.value as
                                  | "AND"
                                  | "OR",
                              }))
                            }
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <label
                            htmlFor="contact-filter-or"
                            className="text-sm text-gray-700"
                          >
                            <span className="font-medium text-blue-600">
                              OU
                            </span>{" "}
                            (au moins un filtre doit correspondre)
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Message si aucun filtre */}
                  {exportConfig.contactFilters.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      Aucun filtre défini. Cliquez sur "Ajouter un filtre" pour
                      commencer.
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={addContactAdvancedFilter}
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Ajouter un filtre</span>
                    </Button>
                    <Button
                      onClick={resetContactAdvancedFilters}
                      variant="outline"
                      disabled={exportConfig.contactFilters.length === 0}
                    >
                      Réinitialiser
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Section Filtres d'entreprise : sur les deux onglets. */}
          {(
          <Card>
            <CardHeader>
              <CardTitle>Filtres d'entreprise</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtres avancés - contenu affiché directement */}
              <div className="mt-4">
                <div className="space-y-4">
                  {/* Grille des filtres */}
                  {exportConfig.companyFilters.map((filter, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 gap-3 items-end"
                    >
                      {/* Champ */}
                      <div className="col-span-4">
                        <Label className="text-sm font-medium text-gray-700">
                          Champ
                        </Label>
                        <Select
                          value={filter.field}
                          onValueChange={(value) =>
                            updateCompanyAdvancedFilter(index, "field", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Champs standards */}
                            <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100">
                              Champs standards
                            </div>
                            {getFilterFieldOptions("company")
                              .filter(
                                (field) => !field.value.includes("custom_"),
                              )
                              .map((field) => (
                                <SelectItem
                                  key={field.value}
                                  value={field.value}
                                >
                                  {field.label}
                                </SelectItem>
                              ))}

                            {/* Champs personnalisés */}
                            {getFilterFieldOptions("company").some((field) =>
                              field.value.includes("custom_"),
                            ) && (
                              <>
                                <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100 mt-1">
                                  Champs personnalisés
                                </div>
                                {getFilterFieldOptions("company")
                                  .filter((field) =>
                                    field.value.includes("custom_"),
                                  )
                                  .map((field) => (
                                    <SelectItem
                                      key={field.value}
                                      value={field.value}
                                    >
                                      {field.label}
                                    </SelectItem>
                                  ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Opérateur */}
                      <div className="col-span-3">
                        <Label className="text-sm font-medium text-gray-700">
                          Condition
                        </Label>
                        <Select
                          value={filter.operator}
                          onValueChange={(value) =>
                            updateCompanyAdvancedFilter(
                              index,
                              "operator",
                              value,
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getFilterOperators(
                              getFilterFieldOptions("company").find(
                                (f) => f.value === filter.field,
                              )?.fieldType || "text",
                            ).map((operator) => (
                              <SelectItem
                                key={operator.value}
                                value={operator.value}
                              >
                                {operator.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Valeur */}
                      <div className="col-span-4">
                        <Label className="text-sm font-medium text-gray-700">
                          Valeur
                        </Label>
                        {renderValueField(filter, index, "company")}
                      </div>

                      {/* Bouton supprimer */}
                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCompanyAdvancedFilter(index)}
                          className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Message si aucun filtre */}
                  {exportConfig.companyFilters.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      Aucun filtre défini. Cliquez sur "Ajouter un filtre" pour
                      commencer.
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={addCompanyAdvancedFilter}
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Ajouter un filtre</span>
                    </Button>
                    <Button
                      onClick={resetCompanyAdvancedFilters}
                      variant="outline"
                      disabled={exportConfig.companyFilters.length === 0}
                    >
                      Réinitialiser
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          )}
      </div>

      {/* Aperçu détaillé des colonnes exportées */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Aperçu détaillé des colonnes exportées</CardTitle>
            <Badge variant="secondary">{previewCount} résultats</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {previewLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    {colonnesExport().map((c) => (
                      <TableHead key={c.cle} className="font-medium">
                        {c.titre}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.slice(0, 10).map((ligne) => (
                    <TableRow key={ligne.cle}>
                      {colonnesExport().map((c) => (
                        <TableCell key={c.cle}>{c.valeur(ligne) || "-"}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {previewData.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucun résultat trouvé avec les filtres actuels
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de sauvegarde */}
      <SaveConfigModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveConfiguration}
        isLoading={isSaving}
      />

      {/* Modal de confirmation de suppression */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              <span>Confirmer la suppression</span>
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la configuration{" "}
              <span className="font-semibold text-gray-900">
                "{configToDelete?.name}"
              </span>{" "}
              ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setConfigToDelete(null);
              }}
              disabled={isDeletingConfig !== null}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteConfiguration}
              disabled={isDeletingConfig !== null}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingConfig !== null ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal d'aperçu de configuration */}
      <Dialog open={isPreviewModalOpen} onOpenChange={setIsPreviewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-blue-600" />
              <span>Aperçu de la configuration</span>
            </DialogTitle>
            <DialogDescription>
              {previewConfigData?.name && (
                <span className="font-medium text-gray-900">
                  "{previewConfigData.name}"
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {previewConfigData && (
            <div className="space-y-6">
              {/* Filtres de contact */}
              {previewConfigData.contactFilters.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">
                      Filtres de contact (
                      {previewConfigData.contactFilters.length})
                    </h3>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                    {previewConfigData.contactFilters.map(
                      (rule: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 text-sm"
                        >
                          <Badge variant="outline" className="bg-white">
                            {rule.field_name}
                          </Badge>
                          <span className="text-gray-600">{rule.operator}</span>
                          <span className="font-medium text-gray-900">
                            {rule.value || "(vide)"}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Filtres d'entreprise */}
              {previewConfigData.companyFilters.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Building2 className="w-4 h-4 text-green-600" />
                    <h3 className="font-semibold text-gray-900">
                      Filtres d'entreprise (
                      {previewConfigData.companyFilters.length})
                    </h3>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 space-y-2">
                    {previewConfigData.companyFilters.map(
                      (rule: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 text-sm"
                        >
                          <Badge variant="outline" className="bg-white">
                            {rule.field_name}
                          </Badge>
                          <span className="text-gray-600">{rule.operator}</span>
                          <span className="font-medium text-gray-900">
                            {rule.value || "(vide)"}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Aucun filtre */}
              {previewConfigData.contactFilters.length === 0 &&
                previewConfigData.companyFilters.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <Filter className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Aucun filtre défini
                    </h3>
                    <p className="text-gray-600">
                      Cette configuration n'a aucun filtre. Tous les contacts et
                      entreprises seront inclus dans l'export.
                    </p>
                  </div>
                )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsPreviewModalOpen(false)}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
